import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import { Add as AddIcon, Edit as EditIcon } from '@material-ui/icons';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { IEBlockPattern } from '../../../models/eBlockPattern';
import { PageFilter } from '../../../state/app';
import { AppState } from '../../../state/configureStore';
import {
  clearEBlockPattern,
  EBlockPatternListFilter,
  EBlockPatternState,
  fetchEBlockPatternList,
} from '../../../state/eBlockPattern';
import {
  DateTimeDisplay,
  PageContainer,
  PageTitle,
  SearchField,
  SortLabel,
  TableLoading,
} from '../../components';
import { useFilter, useHasRole, useTableSorting } from '../../hooks';
import roleRoute from '../roleRoute.config';
import useStyles from './eBlockPattern.style';

const EBlockPatternList: React.FC<RouteComponentProps> = (props) => {
  const { history } = props;
  const classes = useStyles();
  const dispatch = useDispatch();

  const isDisabled = !useHasRole(roleRoute.createEBlockPattern);
  const [filters, setFilters] = useFilter<EBlockPatternListFilter>({});
  const [paging, setPaging] = useState<PageFilter>({
    pageNumber: 0,
    pageSize: 15,
  });
  const [sort, setSort] = useTableSorting({
    orderBy: 'createdAt',
    direction: 'desc',
  });
  const {
    items: eBlockPatterns,
    totalItems,
    loading,
  } = useSelector<AppState, EBlockPatternState>((state) => state.eBlockPattern);

  const handleChangePageNumber = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    setPaging({ ...paging, pageNumber: newPage });
  };

  const handleChangePageSize = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPaging({
      ...paging,
      pageSize: +event.target.value,
      pageNumber: 0,
    });
  };

  const handleChangeFilter = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { target } = event;
    setFilters((prevState) => ({
      ...prevState,
      [target.name]: target.value,
    }));
    setPaging({
      ...paging,
      pageNumber: 0,
    });
  };

  const addNewEBlockPattern = (): void => {
    history.push('/settings/eBlockPatterns/form');
  };

  const editEBlockPattern = (id: IEBlockPattern['id']): void => {
    history.push(`/settings/eBlockPatterns/form/${id}`);
  };

  useEffect(() => {
    dispatch(fetchEBlockPatternList(filters, { ...paging, sort }));
    return function cleanUp() {
      dispatch(clearEBlockPattern());
    };
  }, [dispatch, paging, filters, sort]);

  return (
    <PageContainer>
      <PageTitle>EBlock Patterns</PageTitle>
      <div className={classes.filterContainer}>
        <SearchField
          filterKey="EBlockGroupProduct.CodeAndDesc.Contains"
          filters={filters}
          label="Group Product"
          onChange={handleChangeFilter}
        />
        <SearchField
          filterKey="Code.StartsWith"
          filters={filters}
          label="Code"
          onChange={handleChangeFilter}
        />
        <SearchField
          filterKey="Description.StartsWith"
          filters={filters}
          label="Description"
          onChange={handleChangeFilter}
        />
        <Button
          variant="contained"
          color="primary"
          className={classes.button}
          onClick={addNewEBlockPattern}
          size="large"
        >
          <AddIcon className={classes.iconButton} />
          New EBlock Pattern
        </Button>
      </div>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>No.</TableCell>
            <TableCell>
              <SortLabel
                sort={sort}
                handleSortChange={setSort}
                field="eBlockGroupProduct.code"
              >
                Group Product
              </SortLabel>
            </TableCell>
            <TableCell>
              <SortLabel sort={sort} handleSortChange={setSort} field="code">
                Code
              </SortLabel>
            </TableCell>
            <TableCell>
              <SortLabel sort={sort} handleSortChange={setSort} field="description">
                Description
              </SortLabel>
            </TableCell>
            <TableCell>
              <SortLabel sort={sort} handleSortChange={setSort} field="createdAt">
                CreatedAt
              </SortLabel>
            </TableCell>
            <TableCell>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableLoading isLoading={loading} colSpan={4}>
            {eBlockPatterns.map((eBlockPattern, index) => (
              <TableRow key={eBlockPattern.id}>
                <TableCell>{index + paging.pageSize * paging.pageNumber + 1}</TableCell>
                <TableCell>
                  {eBlockPattern.eBlockGroupProduct
                    ? `${eBlockPattern.eBlockGroupProduct!.code}: ${
                        eBlockPattern.eBlockGroupProduct!.description
                      }`
                    : ''}
                </TableCell>
                <TableCell>{eBlockPattern.code}</TableCell>
                <TableCell>{eBlockPattern.description}</TableCell>
                <TableCell>
                  <DateTimeDisplay value={eBlockPattern.createdAt} />
                </TableCell>
                <TableCell>
                  <IconButton
                    aria-label="edit"
                    size="small"
                    disabled={isDisabled}
                    onClick={() => editEBlockPattern(eBlockPattern.id)}
                  >
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableLoading>
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[15, 25, 35]}
        component="div"
        count={totalItems}
        rowsPerPage={paging.pageSize}
        page={paging.pageNumber}
        onPageChange={handleChangePageNumber}
        onRowsPerPageChange={handleChangePageSize}
      />
    </PageContainer>
  );
};

export default EBlockPatternList;
